import { useState } from 'react'
import { MAPA_VIEWBOX, MAPA_ESTADOS } from '../lib/mapaBrasil'

// Mapa de pedidos por estado. Vive fora do Dashboard porque o extrato de
// faturamento mostra a mesma coisa — e mapa desenhado duas vezes vira duas
// escalas de cor diferentes para o mesmo dado, que é pior que não ter mapa.
//
// `interativo` desliga o hover: no papel não existe mouse, e o rótulo que só
// aparece ao passar por cima simplesmente não sai na impressão. Sem hover, os
// estados pequenos vão para a lista ao lado.

export type EstadoQtd = { uf: string; pedidos: number }

/** Intensidade pelo volume. Cinza = nenhum envio no período — estado sem venda
 *  precisa ficar visivelmente diferente de estado com pouca venda. */
export const CORES_MAPA = ['#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8'] as const

export function MapaEstados({ estados, interativo = true, semUf = 0 }: {
  estados: EstadoQtd[]
  interativo?: boolean
  semUf?: number
}) {
  const [ufAtiva, setUfAtiva] = useState<string | null>(null)
  const ativa = interativo ? ufAtiva : null

  // O endereço nem sempre traz a sigla: o campo `estado` de alguns pedidos vem
  // com o nome por extenso ("São Paulo"), e esses simplesmente não apareciam no
  // mapa — some do desenho e ninguém percebe que sumiu. O nome de cada estado já
  // está em MAPA_ESTADOS, então dá para casar sem tabela nova.
  const semAcento = (t: string) =>
    t.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toUpperCase()
  const porNome = new Map(MAPA_ESTADOS.map(e => [semAcento(e.nome), e.uf]))
  const normalizar = (bruto: string): string | null => {
    const t = semAcento(bruto ?? '')
    if (!t) return null
    if (t.length === 2 && MAPA_ESTADOS.some(e => e.uf === t)) return t
    return porNome.get(t) ?? null
  }

  const porUf = new Map<string, number>()
  let naoReconhecidos = 0
  for (const e of estados) {
    const uf = normalizar(e.uf)
    if (!uf) { naoReconhecidos += e.pedidos; continue }
    porUf.set(uf, (porUf.get(uf) ?? 0) + e.pedidos)
  }

  const maxUf = Math.max(1, ...porUf.values())
  // Denominador = só o que o mapa consegue desenhar. Somar o que não está no
  // desenho faria as fatias nunca fecharem 100% e encolheria cada estado.
  const total = [...porUf.values()].reduce((s, n) => s + n, 0)
  const pct = (n: number) => (total > 0 ? `${Math.round((n / total) * 1000) / 10}%` : '0%')

  const corDoEstado = (n: number) => {
    if (!n) return '#f1f5f9'
    const r = n / maxUf
    if (r > 0.66) return CORES_MAPA[3]
    if (r > 0.33) return CORES_MAPA[2]
    if (r > 0.12) return CORES_MAPA[1]
    return CORES_MAPA[0]
  }

  // Estados cujo rótulo não cabe dentro do contorno. No dashboard eles aparecem
  // no hover; no papel, precisam de uma lista — senão o número deles some.
  const semEspaco = MAPA_ESTADOS
    .filter(e => (porUf.get(e.uf) ?? 0) > 0 && e.folga < 10)
    .map(e => ({ uf: e.uf, nome: e.nome, n: porUf.get(e.uf) ?? 0 }))
    .sort((a, b) => b.n - a.n)

  return (
    <div>
      <svg viewBox={MAPA_VIEWBOX} className="h-auto w-full" role="img" aria-label="Pedidos por estado">
        {MAPA_ESTADOS.map(e => {
          const n = porUf.get(e.uf) ?? 0
          const acesa = ativa === e.uf
          return (
            <path key={e.uf} d={e.path}
              fill={corDoEstado(n)}
              stroke={acesa ? '#0f172a' : '#ffffff'}
              strokeWidth={acesa ? 2 : 0.6}
              className={interativo ? 'cursor-pointer' : undefined}
              onMouseEnter={interativo ? () => setUfAtiva(e.uf) : undefined}
              onMouseLeave={interativo ? () => setUfAtiva(null) : undefined}>
              <title>{`${e.nome} (${e.uf}): ${n} pedido(s)`}</title>
            </path>
          )
        })}

        {/* Rótulo DENTRO do estado, quando a folga até a borda comporta.
            Duas linhas (qtd + %) nos grandes, só a qtd nos médios. */}
        {MAPA_ESTADOS.map(e => {
          const n = porUf.get(e.uf) ?? 0
          if (!n || e.folga < 10 || ativa === e.uf) return null
          const duas = e.folga >= 16
          const escuro = n / maxUf > 0.33
          const cor = escuro ? '#ffffff' : '#1e3a8a'
          return (
            <g key={`t-${e.uf}`} className="pointer-events-none">
              <text x={e.cx} y={e.cy + (duas ? -1 : 4)} textAnchor="middle"
                fontSize={13} fontWeight={700} fill={cor}>
                {n.toLocaleString('pt-BR')}
              </text>
              {duas && (
                <text x={e.cx} y={e.cy + 11} textAnchor="middle"
                  fontSize={10} fill={cor} opacity={0.85}>
                  {pct(n)}
                </text>
              )}
            </g>
          )
        })}

        {/* Destaque do estado sob o mouse — desenhado por último para ficar por
            cima dos vizinhos. É como o estado pequeno mostra o número na tela. */}
        {(() => {
          const e = MAPA_ESTADOS.find(x => x.uf === ativa)
          if (!e) return null
          const n = porUf.get(e.uf) ?? 0
          const texto = `${e.uf} · ${n.toLocaleString('pt-BR')} · ${pct(n)}`
          const largura = texto.length * 7.2 + 14
          const y = e.cy < 60 ? e.cy + 16 : e.cy - 30
          return (
            <g className="pointer-events-none">
              <line x1={e.cx} y1={e.cy} x2={e.cx} y2={y + 22} stroke="#0f172a" strokeWidth={1} />
              <rect x={e.cx - largura / 2} y={y} width={largura} height={22} rx={5} fill="#0f172a" />
              <text x={e.cx} y={y + 15} textAnchor="middle" fontSize={13} fontWeight={600} fill="#ffffff">
                {texto}
              </text>
            </g>
          )
        })()}
      </svg>

      {interativo && (
        <div className="mt-2 flex h-6 items-center justify-center text-sm">
          {ativa ? (
            <span className="font-medium text-gray-800">
              {MAPA_ESTADOS.find(e => e.uf === ativa)?.nome}
              <span className="ml-2 text-gray-500">
                {(porUf.get(ativa) ?? 0).toLocaleString('pt-BR')} pedido(s) · {pct(porUf.get(ativa) ?? 0)}
              </span>
            </span>
          ) : (
            <span className="text-gray-400">passe o mouse para ver estado por estado</span>
          )}
        </div>
      )}

      {/* Legenda dos que não couberam no desenho.
          O estado pequeno mostra o número no hover — e no papel não há hover, o
          número dele simplesmente não sairia. Quem tem o dado e não o mostra é
          igual a quem não tem. */}
      {semEspaco.length > 0 && (
        <div className="mt-2 border-t border-gray-100 pt-2">
          <p className="mb-1 text-[11px] font-medium text-gray-500">Estados sem espaço para o número no mapa</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] sm:grid-cols-3">
            {semEspaco.map(e => (
              <div key={e.uf} className="flex items-baseline justify-between gap-2">
                <span className="truncate text-gray-600">
                  <span className="font-mono font-semibold text-gray-800">{e.uf}</span> {e.nome}
                </span>
                <span className="whitespace-nowrap text-gray-700">
                  <b>{e.n.toLocaleString('pt-BR')}</b> <span className="text-gray-400">{pct(e.n)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 flex items-center justify-between text-[11px] text-gray-400">
        <span className="flex items-center gap-1">
          menos
          {CORES_MAPA.map(c => (
            <span key={c} className="inline-block h-3 w-5 rounded-sm" style={{ backgroundColor: c }} />
          ))}
          mais
        </span>
        <span className="text-amber-600">
          {semUf > 0 && `${semUf.toLocaleString('pt-BR')} sem UF no cadastro`}
          {semUf > 0 && naoReconhecidos > 0 && ' · '}
          {/* Estado que veio escrito de um jeito que o mapa não reconhece precisa
              aparecer como número, não sumir do desenho em silêncio. */}
          {naoReconhecidos > 0 && `${naoReconhecidos.toLocaleString('pt-BR')} com UF não reconhecida`}
        </span>
      </div>
    </div>
  )
}
